'use client';

import './styles/default.scss';
import '@/app/components/zones/layouts/collection/styles/default.scss';
import type { Game as GameDetails } from '@/types/games';
import { getZoneGames } from '@/logic/games/zone-games';
import { parseGamesList } from '@/logic/games/parse-games-list';
import { GameFactoryClient } from '@/app/components/game/game-factory.client';
import { CollectionZoneClient } from '@/app/components/zones/layouts/collection/collection-zone-client';
import { ZoneLayoutProvider } from '@/app/components/zones/contexts/zone-layout-context';
import { useGlobalContext } from '@/store/contexts/global-context';
import { COLLECTION_ZONE_LAYOUT, PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { classes } from '@/utils/classes/classes';

type GameZoneProps = {
    games: GameDetails[];
    zonePosition: any;
    zoneName: string;
};

export const GameZone = ({ games, zoneName, zonePosition }: GameZoneProps) => {
    const { isMobile, region, isLoggedIn, featureFlags } = useGlobalContext();

    if (games.length === 0) {
        return null;
    }

    const gridLayout = (zonePosition ?? PRISMIC_ZONE_LAYOUT.DEFAULT) as PRISMIC_ZONE_LAYOUT;

    if (games.length === 1) {
        const { games: adaptedGames } = parseGamesList(games, PRISMIC_ZONE_LAYOUT.DEFAULT, { region, isMobile });
        const [game] = adaptedGames;
        return (
            <div className="game-zone-container">
                <GameFactoryClient game={game} hideGameInfo={true} priority={false} tracking={{ gamePosition: 0 }} />
            </div>
        );
    }

    const { games: adaptedGames, tracking } = getZoneGames(games, gridLayout, { region, isMobile });

    const hasNewGridLayout = Boolean(featureFlags[PRODUCT_THROTTLES.HAS_NEW_GRID_LAYOUT]);
    const appliedZoneLayout = hasNewGridLayout
        ? COLLECTION_ZONE_LAYOUT[gridLayout]
        : COLLECTION_ZONE_LAYOUT[PRISMIC_ZONE_LAYOUT.DEFAULT];
    const shouldHideGameInfo =
        (appliedZoneLayout !== COLLECTION_ZONE_LAYOUT[PRISMIC_ZONE_LAYOUT.DEFAULT] && isLoggedIn) ||
        appliedZoneLayout === COLLECTION_ZONE_LAYOUT[PRISMIC_ZONE_LAYOUT.VERTICAL_FEATURED_GRID];
    const gridClassName = classes('grid-zone-layout', appliedZoneLayout);

    const gameElements = adaptedGames.map((game, index) => (
        <GameFactoryClient
            key={game.uid}
            tracking={{ gamePosition: index }}
            game={game}
            index={index + 1}
            layout={gridLayout}
            zoneUid={zoneName}
            hideGameInfo={shouldHideGameInfo}
            priority={index === 0}
        />
    ));

    return (
        <div className="game-zone">
            <ZoneLayoutProvider layout={gridLayout}>
                <CollectionZoneClient
                    gameElements={gameElements}
                    gameUids={adaptedGames.map((g) => g.uid)}
                    zoneUid={zoneName}
                    shouldApplyDynamicSorting={false}
                    isMobile={isMobile}
                    isAnimated={false}
                    gridClassName={gridClassName ?? ''}
                    games={tracking}
                    zoneName={zoneName}
                    zoneType="collection"
                    zoneLayout={appliedZoneLayout}
                    bannerId={zoneName}
                />
            </ZoneLayoutProvider>
        </div>
    );
};
